<template>
    <div class="bien-contact-form">
        <h4 class="text-2xl font-spacegrotesk-medium">
            Contacter Pluralis
        </h4>

        <form :id="uid('form')" class="mt-4" @submit.prevent="handleFormSubmit">
            <div class="flex flex-col md:flex-row">
                <div class="flex flex-col md:w-2/5">
                    <div class="flex flex-col">
                        <label
                            class="font-spacegrotesk-regular text-base text-gray-700"
                            :for="uid('fullname')"
                            v-text="'Votre nom'"
                        />
                        <input
                            :id="uid('fullname')"
                            v-model="form.fullName"
                            class="border border-solid border-400 rounded p-2 mt-2"
                            type="text"
                            required
                            autocomplete="name"
                        />
                        <p
                            v-if="validationErrors.fromName"
                            class="text-base text-red-500 mt-2"
                            v-text="error('fromName')"
                        />
                    </div>

                    <div class="flex flex-col mt-4">
                        <label
                            class="font-spacegrotesk-regular text-base text-gray-700"
                            :for="uid('phone')"
                            v-text="'Votre numéro'"
                        />
                        <input
                            :id="uid('phone')"
                            v-model="form.phone"
                            class="border border-solid border-400 rounded p-2 mt-2"
                            type="tel"
                            required
                            autocomplete="tel"
                        />
                    </div>

                    <div class="flex flex-col mt-4">
                        <label
                            class="font-spacegrotesk-regular text-base text-gray-700"
                            :for="uid('email')"
                            v-text="'Votre email'"
                        />
                        <input
                            :id="uid('email')"
                            v-model="form.email"
                            class="border border-solid border-400 rounded p-2 mt-2"
                            type="text"
                            required
                            autocomplete="email"
                        />
                        <p
                            v-if="validationErrors.fromEmail"
                            class="text-base text-red-500 mt-2"
                            v-text="error('fromEmail')"
                        />
                    </div>
                </div>

                <div class="flex flex-col md:w-3/5 mt-4 md:mt-0 md:ml-8">
                    <label
                        class="font-spacegrotesk-regular text-base text-gray-700"
                        :for="uid('message')"
                        v-text="'Votre message'"
                    />
                    <textarea
                        :id="uid('message')"
                        v-model="form.message"
                        class="border border-solid border-400 rounded h-32 md:h-full mt-2 p-2"
                        required
                    ></textarea>
                    <p
                        v-if="validationErrors.message"
                        class="text-base text-red-500 mt-2"
                        v-text="error('message')"
                    />
                </div>
            </div>

            <p class="text-base text-red-500 mt-4" v-text="error('form')" />
        </form>

        <div
            v-if="!isRecaptchaEnabledInTAC"
            class="tac-recaptcha-engage"
            @click="updateIsRecaptchaEnabledInTAC"
        />

        <div class="md:flex md:justify-end mt-6 md:mt-8">
            <vue-recaptcha
                ref="recaptcha"
                class="z-20"
                :sitekey="recaptchaSiteKey"
                :load-recaptcha-script="false"
                badge="bottomright"
                size="invisible"
                @verify="handleCaptchaVerify"
                @expired="handleCaptchaTimeout"
            />

            <button
                class="btn btn--blue w-full md:w-auto"
                type="submit"
                :form="uid('form')"
                :disabled="isSubmitting || !isRecaptchaEnabledInTAC"
            >
                Envoyer
            </button>
        </div>

        <p v-if="hasBeenSuccessfullySent" class="text-base mt-5">
            Votre message est envoyé. Nous prendrons contact avec vous dans les
            meilleurs délais.
        </p>
    </div>
</template>

<script>
import axios from "axios";
import uid from "@/app/mixins/uid";
import recaptchaForm from "@/app/mixins/recaptchaForm";
import "@ungap/url-search-params";
import VueRecaptcha from "vue-recaptcha";

export default {
    name: "BienDetailsContactForm",
    components: { VueRecaptcha },
    mixins: [uid, recaptchaForm],

    props: {
        bien: { type: Object, required: true }
    },

    data() {
        return {
            // eslint-disable-next-line
            validationErrors: {},
            hasBeenSuccessfullySent: false,
            isSubmitting: false,
            form: {
                fullName: "",
                phone: "",
                email: "",
                message: ""
            },
            httpClient: axios.create({
                headers: {
                    "X-Custom-Header": window.Craft.csrfTokenValue
                }
            })
        };
    },

    computed: {
        recaptchaSiteKey() {
            return window._apiKeys.recaptcha;
        }
    },

    methods: {
        error(fieldName) {
            if (!this.validationErrors[fieldName]) {
                return null;
            }

            if (this.validationErrors[fieldName] instanceof Array) {
                return this.validationErrors[fieldName].join(" ");
            }

            return this.validationErrors[fieldName].toString();
        },

        handleCaptchaTimeout() {
            // eslint-disable-next-line no-console
            console.warn("Captcha response expired");
        },

        handleFormSubmit() {
            this.isSubmitting = true;
            this.$refs.recaptcha.execute();
        },

        handleCaptchaVerify(recaptchaResponse) {
            this.isSubmitting = true;
            this.$set(this, "validationErrors", {});

            // Assemble the payload
            const payload = new URLSearchParams();
            payload.append("action", "contact-form/send");
            payload.append("bienId", this.bien.id);
            payload.append("fromName", this.form.fullName);
            payload.append("fromEmail", this.form.email);
            payload.append("formType", "bien");
            payload.append("message[formName]", `Biens #${this.bien.id}`);
            payload.append("message[Téléphone]", this.form.phone);
            payload.append("message[body]", this.form.message);
            payload.append("g-recaptcha-response", recaptchaResponse);
            // eslint-disable-next-line
            payload.append("subject", `Annonce ${this.bien.biensReferencePluralis}`);
            // eslint-disable-next-line
            payload.append(window.Craft.csrfTokenName, window.Craft.csrfTokenValue);

            // POST the payload
            this.httpClient
                .post(document.location.href, payload, {
                    "Content-Type": "application/x-www-form-urlencoded"
                })
                .then(response => {
                    this.hasBeenSuccessfullySent = true;
                })
                .catch(error => {
                    // eslint-disable-next-line no-console
                    console.error(error);

                    if (error.response.status === 400) {
                        this.validationErrors = error.response.data.errors
                        return;
                    }

                    this.$set(
                        this.validationErrors,
                        "form",
                        "Le formulaire a rencontré une erreur, merci de réessayer ultérieurement."
                    );
                })
                .finally(() => {
                    this.isSubmitting = false;
                    this.$refs.recaptcha.reset();
                    this.form = {
                        fullName: "",
                        phone: "",
                        email: "",
                        message: ""
                    }
                });
        }
    }
};
</script>

<style scoped lang="scss">
.bien-contact-form {
    .tac-recaptcha-engage::v-deep .tac_activate {
        padding: 1rem;
    }
}
</style>
